import React, {
  useEffect,
  useRef,
} from 'react';
import type { QuestionnaireClassNames } from '@populateui/ui';
import HeaderLogo from 'components/common/headerLogo';
import { isEmptyString } from 'utils/helper';

export const fixedQuestionnairePageClasses: Partial<QuestionnaireClassNames> = {
  main: '!bg-white/95 !pt-[calc(var(--questionnaire-header-height,14rem)+1rem)] !pb-36 !backdrop-blur-sm md:!pt-[calc(var(--questionnaire-header-height,15rem)+1.25rem)] md:!pb-40',
  footer: 'fixed bottom-0 left-1/2 z-30 w-full max-w-md -translate-x-1/2 border-t border-gray-100 bg-white/95 px-4 pb-[max(1rem,env(safe-area-inset-bottom))] pt-4 backdrop-blur-sm md:max-w-2xl md:px-8',
  footerNav: 'w-full',
  submitButton: {
    root: 'populate-ui-questionnaire-submit_tempo !h-12 !rounded-full !text-sm !font-semibold',
  },
};

type Props = {
  eyebrow?: string;
  answeredCount: number;
  totalCount: number;
};

export default function QuestionnairePageHeader({
  eyebrow,
  answeredCount,
  totalCount,
}: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const safeAnsweredCount = Math.min(answeredCount, totalCount);
  const progressPercent = totalCount > 0
    ? Math.max(0, Math.min(100, (safeAnsweredCount / totalCount) * 100))
    : 0;
  const progressSummary = totalCount > 0
    ? `${safeAnsweredCount} of ${totalCount} answered`
    : null;

  useEffect(() => {
    const root = document.documentElement;
    const element = containerRef.current;

    if (!element) {
      return undefined;
    }

    const syncHeaderHeight = () => {
      root.style.setProperty('--questionnaire-header-height', `${element.offsetHeight}px`);
    };

    syncHeaderHeight();

    const resizeObserver = new ResizeObserver(syncHeaderHeight);
    resizeObserver.observe(element);
    window.addEventListener('resize', syncHeaderHeight);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', syncHeaderHeight);
      root.style.removeProperty('--questionnaire-header-height');
    };
  }, []);

  return (
    <div
      ref={containerRef}
      data-questionnaire-header="true"
      className="fixed left-1/2 top-0 z-30 w-full max-w-md -translate-x-1/2 border-b border-gray-100 bg-white/95 px-4 pb-4 pt-4 backdrop-blur-sm md:max-w-2xl md:px-8"
    >
      <div className="mx-auto flex w-full flex-col gap-2.5">
        <HeaderLogo className="header-logo--compact" />

        <div className="rounded-2xl border border-gray-100 bg-white/90 px-4 py-3 shadow-[0_10px_24px_rgba(15,23,42,0.05)]">
          <div className="flex flex-col gap-3">
            <div className="flex items-center justify-between gap-3">
              {!isEmptyString(eyebrow) && (
                <p className="inline-flex items-center rounded-full bg-populate-green/10 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-populate-green">
                  {eyebrow}
                </p>
              )}

              {progressSummary && (
                <p className="shrink-0 text-xs font-semibold text-gray-600">
                  {progressSummary}
                </p>
              )}
            </div>

            {totalCount > 0 ? (
              <div className="h-2 overflow-hidden rounded-full bg-populate-green/15">
                <div
                  className="h-full rounded-full bg-gradient-to-r from-[#1ea89c] to-[#2db5aa] transition-[width] duration-300 ease-out"
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
